import { assertNever } from '@omnea-app/legacy/utils/type-guard/assertNever'
import { RequestTopLevelFilterKey } from '@omnea-app/requests/requestsFrontendClient'
import type { GridRowId } from '@mui/x-data-grid-premium'

export const generateRequestsListRoute = (mode?: RequestTopLevelFilterKey) => {
  if (!mode) {
    // The requests page will determine the tab based on local storage or user's role
    return '/requests'
  }
  switch (mode) {
    case 'mine':
      return '/requests/mine'
    case 'all':
      return '/requests/all'
    case 'pending':
      return '/requests/pending'
    case 'minePending':
      return '/requests/my-pending'
    case 'delayed':
      return '/requests/delayed'
    case 'mineDelayed':
      return '/requests/my-delayed'
    case 'completed':
      return '/requests/completed'
    case 'mineCompleted':
      return '/requests/my-completed'
    case 'cancelled':
      return '/requests/cancelled'
    case 'mineCancelled':
      return '/requests/my-cancelled'
    case 'paused':
      return '/requests/paused'
    case 'minePaused':
      return '/requests/my-paused'
    case 'rejected':
      return '/requests/rejected'
    case 'following':
      return '/requests/following'
    case 'owned':
      return '/requests/owned'
    case 'rejected-tasks':
      return '/requests/rejected-tasks'
    case 'errored-tasks':
      return '/requests/errored-tasks'
    default: {
      assertNever(mode)
      return `/requests/${mode}`
    }
  }
}

export const generateRequestDetailRoute = (tab: RequestTabs, requestId: string | GridRowId, taskId?: string, formId?: string) => {
  switch (tab) {
    case RequestTabs.OVERVIEW:
      return `/requests/detail/${requestId}/overview`
    case RequestTabs.INTAKE_FORM:
      return `/requests/detail/${requestId}/intake-form`
    case RequestTabs.SUBMISSIONS:
      return `/requests/detail/${requestId}/submissions${formId ? `/${formId}` : ''}`
    case RequestTabs.RENEWAL_ASSESSMENT_FORM:
      return `/requests/detail/${requestId}/renewal-assessment`
    case RequestTabs.MY_TASKS:
      return `/requests/detail/${requestId}/my-tasks${taskId ? `/${taskId}` : ''}`
    case RequestTabs.DOCUMENTS:
      return `/requests/detail/${requestId}/documents`
    case RequestTabs.RISKS:
      return `/requests/detail/${requestId}/risks`
    case RequestTabs.RFX:
      return `/requests/detail/${requestId}/rfx`
    case RequestTabs.WORKFLOW_SNAPSHOT:
      return `/requests/detail/${requestId}/workflow`
    default: {
      assertNever(tab)
      throw new Error(`Unhandled tab: ${tab}`)
    }
  }
}

export const rfxSections = ['activity', 'settings', 'suppliers', 'questionnaires', 'responses', 'results'] as const
export type RfxSectionType = (typeof rfxSections)[number]

export const generateRfxSectionRoute = (sectionType: RfxSectionType, requestId: string) => {
  return `/requests/detail/${requestId}/rfx/${sectionType}`
}

export const generateRfxSettingsSubRoute = (sub: 'deadlines' | 'ai-scoring', requestId: string) => {
  return `${generateRfxSectionRoute('settings', requestId)}/${sub}`
}

export const requestDetailUrlTabs = [
  'overview',
  'my-tasks',
  'intake-form',
  'submissions',
  'renewal-assessment',
  'documents',
  'risks',
  'rfx',
  'workflow',
] as const
export type RequestDetailUrlTab = (typeof requestDetailUrlTabs)[number]

export const parseRequestDetailRoute = (tab: RequestDetailUrlTab) => {
  switch (tab) {
    case 'overview':
      return RequestTabs.OVERVIEW
    case 'intake-form':
      return RequestTabs.INTAKE_FORM
    case 'submissions':
      return RequestTabs.SUBMISSIONS
    case 'renewal-assessment':
      return RequestTabs.RENEWAL_ASSESSMENT_FORM
    case 'my-tasks':
      return RequestTabs.MY_TASKS
    case 'documents':
      return RequestTabs.DOCUMENTS
    case 'risks':
      return RequestTabs.RISKS
    case 'rfx':
      return RequestTabs.RFX
    case 'workflow':
      return RequestTabs.WORKFLOW_SNAPSHOT
    default: {
      assertNever(tab)
      return RequestTabs.OVERVIEW
    }
  }
}

export enum RequestTabs {
  OVERVIEW = 'OVERVIEW',
  INTAKE_FORM = 'INTAKE_FORM',
  RENEWAL_ASSESSMENT_FORM = 'RENEWAL_ASSESSMENT_FORM',
  SUBMISSIONS = 'SUBMISSIONS',
  MY_TASKS = 'MY_TASKS',
  DOCUMENTS = 'DOCUMENTS',
  RISKS = 'RISKS',
  RFX = 'RFX',
  WORKFLOW_SNAPSHOT = 'WORKFLOW_SNAPSHOT',
}
